import { apiService } from './ApiService';

// Chat log types
interface ChatLog {
  id: string;
  user_message: string;
  bot_response: string;
  timestamp: string;
  session_id?: string;
  user_id?: string;
  metadata?: Record<string, any>;
}

class ChatLogService {
  private requestCache = new Map<string, {
    data: any;
    timestamp: number;
  }>();
  private readonly CACHE_DURATION = 30 * 1000; // 30 seconds cache for API requests

  // Get all chat logs with request caching
  async getChatLogs(page = 1, pageSize = 10, search?: string, userId?: string): Promise<{
    chatLogs: ChatLog[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    try {
      // Create cache key
      const cacheKey = `getMongoChatLogs_${page}_${pageSize}_${search || 'all'}_${userId || 'all'}`;
      const cachedRequest = this.requestCache.get(cacheKey);
      const now = Date.now();
      
      // Return cached data if available and not expired
      if (cachedRequest && (now - cachedRequest.timestamp) < this.CACHE_DURATION) {
        return cachedRequest.data;
      }

      const params: any = {
        page,
        limit: pageSize
      };

      if (search) params.search = search;
      if (userId) params.user_id = userId;

      console.log('🚀 Making MongoDB API request to /api/v1/mongodb/chatlogs/chat with params:', params);
      const startTime = performance.now();
      
      const response = await apiService.get('/api/v1/mongodb/chatlogs/chat', params);
      
      const endTime = performance.now();
      console.log(`⏱️ MongoDB API request completed in ${(endTime - startTime).toFixed(2)}ms`);
      
      if (response.data) {
        const apiResponse = response.data as any;
        const chatLogs = apiResponse.data || [];
        const pagination = apiResponse.pagination || {};
        
        // Map the API response fields to match our interface
        const mappedChatLogs = chatLogs.map((item: any) => ({
          id: item.conversation_id || item.id,
          session_id: item.conversation_id,
          user_id: item.user_id,
          user_message: item.user_message,
          bot_response: item.bot_response,
          timestamp: item.timestamp,
          metadata: item.metadata || {}
        }));
        
        const result = {
          chatLogs: mappedChatLogs,
          total: pagination.total_items || chatLogs.length,
          page: pagination.current_page || page,
          pageSize: pagination.items_per_page || pageSize
        };
        
        // Cache the result
        this.requestCache.set(cacheKey, {
          data: result,
          timestamp: now
        });
        
        // Cleanup old cache entries (keep only last 5)
        if (this.requestCache.size > 5) {
          const entries = Array.from(this.requestCache.entries());
          entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
          this.requestCache.clear();
          entries.slice(0, 5).forEach(([key, value]) => {
            this.requestCache.set(key, value);
          });
        }
        
        return result;
      }
      
      throw new Error(response.error || 'Failed to fetch chat logs from MongoDB');
    } catch (error) {
      console.error('Get MongoDB chat logs error:', error);
      throw error;
    }
  }

  // Get chat log by ID
  async getChatLog(id: string): Promise<ChatLog> {
    try {
      const response = await apiService.get<ChatLog>(`/api/v1/mongodb/chatlogs/chat/${id}`);
      
      if (response.data) {
        return response.data;
      }
      
      throw new Error(response.error || 'Chat log not found in MongoDB');
    } catch (error) {
      console.error('Get MongoDB chat log error:', error);
      throw error;
    }
  }

  // Create chat log
  async createChatLog(chatLogData: Partial<ChatLog>): Promise<ChatLog> {
    try {
      const response = await apiService.post<ChatLog>('/api/v1/mongodb/chatlogs/chat', chatLogData);
      
      if (response.data) {
        return response.data;
      }
      
      throw new Error(response.error || 'Failed to create chat log in MongoDB');
    } catch (error) {
      console.error('Create MongoDB chat log error:', error);
      throw error;
    }
  }

  // Update chat log
  async updateChatLog(id: string, chatLogData: Partial<ChatLog>): Promise<ChatLog> {
    try {
      const response = await apiService.put<ChatLog>(`/api/v1/mongodb/chatlogs/chat/${id}`, chatLogData);
      
      if (response.data) {
        return response.data;
      }
      
      throw new Error(response.error || 'Failed to update chat log in MongoDB');
    } catch (error) {
      console.error('Update MongoDB chat log error:', error);
      throw error;
    }
  }

  // Delete chat log
  async deleteChatLog(id: string): Promise<boolean> {
    try {
      const response = await apiService.delete(`/api/v1/mongodb/chatlogs/chat/${id}`);
      return response.status === 200;
    } catch (error) {
      console.error('Delete MongoDB chat log error:', error);
      throw error;
    }
  }

  // Delete chat logs by user IDs
  async deleteChatLogsByUserIds(userIds: string[]): Promise<{ 
    success: boolean; 
    total_deleted: number; 
    failed_deletions: Array<{user_id: string; error: string}>; 
    message: string; 
  }> {
    try {
      console.log('🗑️ Deleting chat logs for user IDs:', userIds);
      
      const response = await apiService.deleteWithData('/api/v1/mongodb/chatlogs/delete-by-user-ids', {
        user_ids: userIds
      });
      
      if (response.data) {
        // Clear cache after successful deletion
        this.clearCache();
        
        const result = response.data as any;
        return {
          success: result.success,
          total_deleted: result.total_deleted,
          failed_deletions: result.failed_deletions || [],
          message: result.success ? 'Deletion completed' : 'Some deletions failed'
        };
      }
      
      throw new Error(response.error || 'Failed to delete chat logs by user IDs from MongoDB');
    } catch (error) {
      console.error('Delete MongoDB chat logs by user IDs error:', error);
      throw error;
    }
  }

  // Get unique users for filter dropdown
  async getUniqueUsers(): Promise<string[]> {
    try {
      const response = await apiService.get('/api/v1/mongodb/chatlogs/users');
      
      if (response.data) {
        const result = response.data as any;
        return result.users || [];
      }
      
      throw new Error(response.error || 'Failed to fetch users from MongoDB');
    } catch (error) {
      console.error('Get unique MongoDB users error:', error);
      throw error;
    }
  }

  // Export chat logs
  async exportChatLogs(format = 'csv', startDate?: string, endDate?: string): Promise<void> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('format', format);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const url = `/api/v1/mongodb/chatlogs/export?${params.toString()}`;
      await apiService.downloadFile(url, `mongodb_chatlogs.${format}`);
    } catch (error) {
      console.error('Export MongoDB chat logs error:', error);
      throw error;
    }
  }

  // Clear cache method
  clearCache(): void {
    this.requestCache.clear();
    console.log('🧹 MongoDB request cache cleared');
  }
}

export const chatLogService = new ChatLogService();
export default ChatLogService;
